import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Capability Map Provider — fetches the per-user orchestration object once
 * per session and exposes it to the entire frontend tree.
 *
 * Architectural laws honored:
 *   L2  — the map is the deterministic output of role × org × org-config.
 *         The frontend never edits it; it re-fetches when invalidated.
 *   L3  — every visibility decision in the UI reads from this single source.
 *   L9  — fail-closed: while loading, on error, or before login,
 *         `visible_modules` is empty. Children render nothing module-bound.
 *   §3.6 — calm "Loading your environment…" stance: the provider exposes
 *         `loading` so the shell can paint the empty state cleanly rather
 *         than briefly flashing a fully-populated UI.
 */
const CapabilityContext = createContext(null);

const EMPTY_MAP = Object.freeze({
    user_id: "",
    role: "",
    org_config_version: "v1",
    visible_modules: [],
    scope: { organization_ids: [], region_ids: [], location_ids: [], card_ids: [], unbounded: false },
    governance: { read: false, write: false, lock: false, override: false, propagate: false, break_glass: false },
    analytics_tier: "none",
    audit_visibility: "none",
    language: "en",
    demo_overlay: null,
});

export const CapabilityProvider = ({ children }) => {
    const { user } = useAuth();
    const [map, setMap] = useState(EMPTY_MAP);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchMap = useCallback(async () => {
        // L9 — fail-closed when there is no authenticated user.
        if (!user?.id) {
            setMap(EMPTY_MAP);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await api.get("/operational-base/capability-map");
            setMap(res.data || EMPTY_MAP);
        } catch (err) {
            // Fail-closed (§3.6): show nothing rather than too much.
            setMap(EMPTY_MAP);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchMap();
    }, [fetchMap]);

    // v4.8.1 — Re-fetch the map whenever Master Demo Mode flips. The
    // backend's compute_capability_map() filters visible_modules to the
    // curated demo allowlist when demo_mode_active is true, so the
    // sidebar/tile grid must refresh on toggle without a page reload.
    useEffect(() => {
        const handler = () => fetchMap();
        window.addEventListener("ob:demo-mode-changed", handler);
        return () => window.removeEventListener("ob:demo-mode-changed", handler);
    }, [fetchMap]);

    const value = useMemo(() => ({
        map,
        loading,
        error,
        refresh: fetchMap,
        // Convenience predicate consumed by enforcement primitives.
        hasModule: (name) => Array.isArray(map?.visible_modules) &&
            map.visible_modules.some((m) => m.name === name),
        getModule: (name) => (map?.visible_modules || []).find((m) => m.name === name) || null,
    }), [map, loading, error, fetchMap]);

    return (
        <CapabilityContext.Provider value={value}>
            {children}
        </CapabilityContext.Provider>
    );
};

export const useCapabilityContext = () => {
    const ctx = useContext(CapabilityContext);
    if (!ctx) {
        // Defensive: render fail-closed when used outside the provider.
        // This should never happen in production wiring.
        return { map: EMPTY_MAP, loading: false, error: null, refresh: () => {}, hasModule: () => false, getModule: () => null };
    }
    return ctx;
};
